import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { axisName, useT } from '../lib/i18n';
import { alert, confirm, haptic } from '../lib/telegram';
import { fmtLongDay, fmtTime } from '../lib/time';
import { GrowArea } from './GrowArea';
import { PinnedSheet } from './SetSheet';

/**
 * The coach's exercise library (src/coach/drills.ts): the list in Settings
 * → «Упражнения», suggestions under a plan field, and «Что помогало с этим
 * у других» from a recurring theme in a player's diary.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setDrillsToken = (t: string) => { authToken = t; };
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: { ...(init.body ? { 'content-type': 'application/json' } : {}), authorization: `Bearer ${authToken}` } });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error ?? `http_${res.status}`); }
  return res.json();
}

interface Description { how: string; volume: string; watch: string; harder: string }
interface Drill {
  id: string; name: string; name_other: string | null; aliases: string[]; skills: string[]; general: boolean;
  uses: number; players: number; k: number; m: number; description: Description | null;
}
interface Axis { id: string; name: string; name_ru: string | null }

function useAxes() {
  const [axes, setAxes] = useState<Axis[]>([]);
  useEffect(() => { api<Axis[]>('/api/rating-axes').then(setAxes).catch(() => setAxes([])); }, []);
  return axes;
}

/** «использовано 4 раза · 3 игрока», and «у 2 из 3 оценка выросла» when there are 2+ comparisons. */
function Stats({ d }: { d: Drill }) {
  const t = useT();
  return (
    <>
      <span className="dr-stat">{t('dr.used', { n: d.uses, times: t.word('dr.times', d.uses), m: d.players, players: t.word('dr.players', d.players) })}</span>
      {d.m >= 2 && <span className="dr-result">{t('dr.result', { k: d.k, m: d.m })}</span>}
    </>
  );
}

function SkillChips({ d, axes }: { d: Drill; axes: Axis[] }) {
  const t = useT();
  if (d.general) return <span className="dr-chip dr-chip--general">{t('dr.general')}</span>;
  return <>{d.skills.map((s) => { const a = axes.find((x) => x.id === s); return a ? <span key={s} className="dr-chip">{axisName(a)}</span> : null; })}</>;
}

const BLOCKS = [
  ['how', 'dr.how', 'dr.howPh'], ['volume', 'dr.volume', 'dr.volumePh'],
  ['watch', 'dr.watch', 'dr.watchPh'], ['harder', 'dr.harder', 'dr.harderPh'],
] as const;
const firstLine = (d: Description | null) => {
  if (!d) return '';
  for (const k of ['how', 'volume', 'watch', 'harder'] as const) {
    const line = d[k]?.split('\n').map((x) => x.trim()).find(Boolean);
    if (line) return line;
  }
  return '';
};

type Filter = 'all' | 'general' | 'none' | string;
const capitalised = (x: string) => x.charAt(0).toLocaleUpperCase() + x.slice(1);

/**
 * Settings → «Упражнения»: search, chips by skill with counts, the list
 * grouped by skill. Each row: the name, the other-language name, the
 * description's first line (or «Без описания · добавить»), and on the
 * right the result and how often it was used.
 */
export function DrillLibrarySheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const axes = useAxes();
  const [data, setData] = useState<{ drills: Drill[]; pending: number } | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [building, setBuilding] = useState(false);
  const [opened, setOpened] = useState<Drill | null>(null);
  const [tidying, setTidying] = useState(false);
  const [sharingOpen, setSharingOpen] = useState(false);
  const [cat, setCat] = useState<CatalogueState | null>(null);
  const loadCat = () => api<CatalogueState>('/api/coach/catalogue').then(setCat).catch(() => setCat(null));
  useEffect(() => { loadCat(); }, []);
  const load = () => api<{ drills: Drill[]; pending: number }>(`/api/coach/drills${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`)
    .then(setData).catch(() => setData({ drills: [], pending: 0 }));
  useEffect(() => { const h = setTimeout(load, search ? 250 : 0); return () => clearTimeout(h); }, [search]);

  async function build() {
    setBuilding(true);
    try {
      const r = await api<{ plans: number; created: number }>('/api/coach/drills/build', { method: 'POST', body: '{}' });
      haptic.success();
      await alert(t('dr.built', { plans: r.plans, created: r.created }));
      load();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'ai_not_configured' ? t('dr.noAi') : t('co.failed', { message: err.message }));
    } finally { setBuilding(false); }
  }

  const drills = data?.drills ?? [];
  // A drill's section: its first skill in the coach's order, «общее», or none.
  const order = new Map(axes.map((a, i) => [a.id, i]));
  const primary = (d: Drill) => (d.general ? 'general' : [...d.skills].sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99))[0] ?? 'noskill');
  const counts = new Map<string, number>();
  for (const d of drills) {
    if (d.general) counts.set('general', (counts.get('general') ?? 0) + 1);
    for (const s of d.skills) counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const noDesc = drills.filter((d) => !d.description).length;
  const shown = drills.filter((d) => filter === 'all' ? true : filter === 'none' ? !d.description
    : filter === 'general' ? d.general : d.skills.includes(filter));
  const sections = [...axes.map((a) => ({ key: a.id, title: axisName(a) })),
    { key: 'general', title: t('dr.general') }, { key: 'noskill', title: t('dr.noSkill') }]
    .map((sec) => ({ ...sec, rows: shown.filter((d) => (filter !== 'all' && filter !== 'none' ? sec.key === filter : primary(d) === sec.key)) }))
    .filter((sec) => sec.rows.length);

  // Back from the review: the list again from scratch, not the names from before it.
  if (tidying) return <TidySheet axes={axes} onClose={() => { setTidying(false); setData(null); load(); }} />;
  if (sharingOpen) return <CatalogueSheet onClose={() => { setSharingOpen(false); loadCat(); load(); }} />;
  if (opened) return <DrillCard d={opened} all={drills} axes={axes} onClose={() => { setOpened(null); load(); }} />;
  const chip = (key: Filter, label: string, extra = '') => (
    <button key={key} className={`chip dr-filter${extra}`} aria-pressed={filter === key} onClick={() => { haptic.tap(); setFilter(key); }}>{label}</button>
  );
  return (
    <PinnedSheet title={t('dr.title')} onClose={onClose} full
      footer={<>
        <button className="paybar__opt" disabled={building} onClick={build}>
          {building ? t('dr.building') : data?.pending ? `${t('dr.build')} · ${data.pending}` : t('dr.build')}
        </button>
        <button className="paybar__opt" disabled={!drills.length} onClick={() => { haptic.tap(); setTidying(true); }}>✨ {t('dr.tidy')}</button>
      </>}>
      {cat && (
        <button className={`cat-card${cat.shares ? ' cat-card--on' : ''}`} onClick={() => { haptic.tap(); setSharingOpen(true); }}>
          <span className="cat-card__main">
            <b>{t('cat.share')}</b>
            <small>{cat.shares ? t('cat.shareOn', { n: cat.inCatalogue, m: cat.drills }) : t('cat.shareOff')}</small>
            {cat.badge && <small className="cat-badge">{t('cat.badge', { name: cat.badge.name })}</small>}
          </span>
          <span className={`cat-switch${cat.shares ? ' cat-switch--on' : ''}`} aria-hidden="true"><i /></span>
        </button>
      )}
      <input className="clients__search dr-search" type="search" value={search} placeholder={t('dr.search')} onChange={(e) => setSearch(e.target.value)} />
      {drills.length > 0 && (
        <div className="dr-filters">
          {chip('all', t('dr.all', { n: drills.length }))}
          {axes.filter((a) => counts.get(a.id)).map((a) => chip(a.id, `${axisName(a)} ${counts.get(a.id)}`))}
          {counts.get('general') ? chip('general', `${capitalised(t('dr.general'))} ${counts.get('general')}`) : null}
          {noDesc > 0 && chip('none', t('dr.noDesc', { n: noDesc }), ' dr-filter--amber')}
        </div>
      )}
      {!data ? <div className="skeleton" /> : drills.length === 0 ? (
        <div className="card__when" style={{ marginTop: 12 }}>{search ? t('dr.nothing') : t('dr.empty')}</div>
      ) : sections.map((sec) => (
        <section key={sec.key} className="dr-group">
          <div className="dr-group__head">{sec.title}<span>{sec.rows.length}</span></div>
          {sec.rows.map((d) => (
            <button key={d.id} className="dr-row dr-row--lib" onClick={() => { haptic.tap(); setOpened(d); }}>
              <span className="dr-row__main">
                <b className="dr-row__name">{d.name}</b>
                {d.name_other && <span className="dr-row__other">{d.name_other}</span>}
                {d.description
                  ? <span className="dr-row__desc">{firstLine(d.description)}</span>
                  : <span className="dr-row__desc dr-row__desc--none">{t('dr.noDescRow')}</span>}
              </span>
              <span className="dr-row__side">
                {d.m >= 2 && <b className="dr-row__res">{t('dr.resultShort', { k: d.k, m: d.m })}</b>}
                <span>{t('dr.usesShort', { n: d.uses })}</span>
              </span>
            </button>
          ))}
        </section>
      ))}
    </PinnedSheet>
  );
}

/**
 * The drill card, full screen. Read first: names, skill, the four blocks
 * of the description and the stats; «Изменить» opens everything for
 * editing (merge and archive too). «✨ Черновик описания с AI» fills the
 * four blocks for the coach to check before saving; «В план занятия» adds
 * the drill to an upcoming session's plan.
 */
function DrillCard({ d, all, axes, onClose }: { d: Drill; all: Drill[]; axes: Axis[]; onClose: () => void }) {
  const t = useT();
  const empty: Description = { how: '', volume: '', watch: '', harder: '' };
  // A drill with no description opens ready to write one.
  const [editing, setEditing] = useState(!d.description);
  const [name, setName] = useState(d.name);
  const [nameOther, setNameOther] = useState(d.name_other ?? '');
  const [skills, setSkills] = useState<string[]>(d.skills);
  const [general, setGeneral] = useState(d.general);
  const [desc, setDesc] = useState<Description>({ ...empty, ...(d.description ?? {}) });
  const [into, setInto] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDrafted, setAiDrafted] = useState(false);
  const [picking, setPicking] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); haptic.success(); onClose(); }
    catch (err: any) { haptic.error(); await alert(err.message === 'taken' ? t('dr.taken') : t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  };
  const toggle = (id: string) => setSkills(skills.includes(id) ? skills.filter((s) => s !== id) : [...skills, id].slice(-2));
  const target = all.find((x) => x.id === into);
  // Skills show in two rows; «Все навыки» opens the rest when they don't fit.
  const chipsRef = useRef<HTMLDivElement>(null);
  const [allSkills, setAllSkills] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = chipsRef.current;
    if (el && !allSkills) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [axes, allSkills, editing]);

  async function aiDraft() {
    setAiBusy(true);
    try {
      const r = await api<Description>(`/api/coach/drills/${d.id}/describe`, { method: 'POST', body: '{}' });
      setDesc({ ...empty, ...r });
      setAiDrafted(true);
      setEditing(true);
      haptic.success();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'ai_not_configured' ? t('dr.noAi') : t('co.failed', { message: err.message }));
    } finally { setAiBusy(false); }
  }
  const save = () => run(() => api(`/api/coach/drills/${d.id}`, { method: 'PUT', body: JSON.stringify({
    name, name_other: nameOther, skills: general ? [] : skills, general, description: desc,
  }) }));
  const aiButton = (
    <button className="paybar__opt" disabled={aiBusy} onClick={aiDraft}>{aiBusy ? t('dr.aiWorking') : t('dr.aiDraft')}</button>
  );

  if (picking) return <SessionPicker drill={d} onClose={() => setPicking(false)} />;
  return (
    <PinnedSheet title={t('dr.edit')} onClose={onClose} full
      action={!editing && <button className="dr-editbtn" onClick={() => { haptic.tap(); setEditing(true); }}>{t('dr.editBtn')}</button>}
      footer={editing ? <>
        {/* Back to the card as it was, with or without a description (its stats are there). */}
        <button className="paybar__opt" onClick={() => { setEditing(false); setDesc({ ...empty, ...(d.description ?? {}) }); setAiDrafted(false);
          setName(d.name); setNameOther(d.name_other ?? ''); setSkills(d.skills); setGeneral(d.general); }}>{t('dr.cancel')}</button>
        <button className="savebtn" disabled={busy || !name.trim()} onClick={save}>{t('dr.save')}</button>
      </> : <>
        {aiButton}
        <button className="savebtn" onClick={() => { haptic.tap(); setPicking(true); }}>{t('dr.toSession')}</button>
      </>}>
      {editing ? (<>
        <label className="mf"><span className="mf__label">{t('dr.name')}</span>
          <input className="notearea dr-input dr-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></label>
        <label className="mf"><span className="mf__label">{t('dr.nameOther')}</span>
          <input className="notearea dr-input" value={nameOther} maxLength={120} onChange={(e) => setNameOther(e.target.value)} /></label>
        {d.aliases.length > 0 && <div className="card__when">{t('dr.aliases', { list: d.aliases.join(', ') })}</div>}
        <div className="mf"><span className="mf__label">{t('dr.skills')}</span>
          <div ref={chipsRef} className={`dr-skills${allSkills ? ' dr-skills--all' : ''}`}>
            {axes.map((a) => (
              <button key={a.id} className="chip" aria-pressed={!general && skills.includes(a.id)} disabled={general}
                onClick={() => toggle(a.id)}>{axisName(a)}</button>
            ))}
            <button className="chip" aria-pressed={general} onClick={() => setGeneral(!general)}>{t('dr.general')}</button>
          </div>
          {(overflows || allSkills) && (
            <button className="sess__link dr-more" onClick={() => setAllSkills(!allSkills)}>
              {allSkills ? t('dr.skillsLess') : t('dr.skillsMore')}
            </button>
          )}
        </div>
        <div className="dr-ai">{aiButton}{aiDrafted && <div className="dr-ai__note">{t('dr.aiDrafted')}</div>}</div>
        {BLOCKS.map(([k, label, ph]) => (
          <label key={k} className="mf"><span className="mf__label">{t(label)}</span>
            <GrowArea className="notearea" rows={k === 'volume' ? 1 : 3} value={desc[k]} placeholder={t(ph)}
              onChange={(e) => setDesc({ ...desc, [k]: e.target.value })} /></label>
        ))}
        <div className="dr-card"><span className="mf__label">{t('dr.mergeInto')}</span>
          <select className="notearea dr-input" value={into} onChange={(e) => setInto(e.target.value)}>
            <option value="">—</option>
            {all.filter((x) => x.id !== d.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          {target && (
            <button className="paybar__opt" style={{ marginTop: 8, width: '100%' }} disabled={busy}
              onClick={async () => { if (await confirm(t('dr.mergeConfirm', { a: d.name, b: target.name }))) await run(() => api(`/api/coach/drills/${d.id}/merge`, { method: 'POST', body: JSON.stringify({ into }) })); }}>
              {t('dr.merge')}
            </button>
          )}
        </div>
        <button className="dr-archive" disabled={busy}
          onClick={async () => { if (await confirm(t('dr.archiveConfirm', { name: d.name }))) await run(() => api(`/api/coach/drills/${d.id}/archive`, { method: 'POST', body: '{}' })); }}>
          {t('dr.archive')}
        </button>
      </>) : (<>
        <div className="dr-head">
          <h2 className="dr-head__name">{d.name}</h2>
          {d.name_other && <div className="dr-head__other">{d.name_other}</div>}
          <div className="dr-chips"><SkillChips d={d} axes={axes} /></div>
        </div>
        {BLOCKS.map(([k, label]) => (
          <section key={k} className="dr-block">
            <div className="dr-block__head">{t(label)}</div>
            {desc[k].trim() ? <p>{desc[k]}</p> : <p className="dr-block__empty">{t('dr.notFilled')}</p>}
          </section>
        ))}
        <section className="dr-block">
          <div className="dr-block__head">{t('dr.stats')}</div>
          <div className="dr-stats">
            <div><span className="mf__label">{t('dr.statsOwn')}</span><div className="dr-meta"><Stats d={d} /></div></div>
            <div><span className="mf__label">{t('dr.statsOthers')}</span><OthersLine drillId={d.id} /></div>
          </div>
        </section>
      </>)}
    </PinnedSheet>
  );
}

/** «В план занятия»: an upcoming session, and the drill goes into its plan. */
function SessionPicker({ drill, onClose }: { drill: Drill; onClose: () => void }) {
  const t = useT();
  const [list, setList] = useState<{ id: string; starts_at: string; title: string; names: string[]; has_plan: boolean }[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api<{ sessions: any[] }>('/api/coach/drills/sessions').then((r) => setList(r.sessions)).catch(() => setList([])); }, []);
  async function pick(id: string) {
    setBusy(true);
    try {
      const r = await api<{ startsAt: string }>(`/api/coach/drills/${drill.id}/to-session`, { method: 'POST', body: JSON.stringify({ sessionId: id }) });
      haptic.success();
      await alert(t('dr.added', { when: `${fmtLongDay(new Date(r.startsAt))}, ${fmtTime(r.startsAt)}` }));
      onClose();
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  return (
    <PinnedSheet title={t('dr.pickSession')} onClose={onClose}
      footer={<button className="paybar__opt" onClick={onClose}>{t('dr.cancel')}</button>}>
      <div className="card__when">«{drill.name}»</div>
      {!list ? <div className="skeleton" /> : list.length === 0 ? <div className="card__when">{t('dr.noSessions')}</div> : (
        <div className="dr-list">
          {list.map((s) => (
            <button key={s.id} className="dr-row" disabled={busy} onClick={() => pick(s.id)}>
              <b>{fmtLongDay(new Date(s.starts_at))}, {fmtTime(s.starts_at)}</b>
              <span className="dr-row__other">{s.names.join(', ')}{s.has_plan ? ` · ${t('dr.hasPlan')}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </PinnedSheet>
  );
}

/**
 * Under a plan field: names from the library matching what is being typed
 * (the text after the last comma or line break, from 3 characters).
 * Tapping one puts it in place of that text. Free text is never held up.
 */
export function DrillSuggest({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [names, setNames] = useState<string[]>([]);
  const cut = Math.max(value.lastIndexOf('\n'), value.lastIndexOf(','), value.lastIndexOf(';')) + 1;
  const fragment = value.slice(cut).trim();
  useEffect(() => {
    if (fragment.length < 3) { setNames([]); return; }
    const h = setTimeout(() => {
      api<{ names: string[] }>(`/api/coach/drills/suggest?q=${encodeURIComponent(fragment)}`)
        .then((r) => setNames(r.names.filter((n) => n.toLowerCase() !== fragment.toLowerCase()).slice(0, 6))).catch(() => setNames([]));
    }, 200);
    return () => clearTimeout(h);
  }, [fragment]);
  if (!names.length) return null;
  return (
    <div className="dr-suggest">
      {names.map((n) => (
        <button key={n} type="button" className="chip" onMouseDown={(e) => e.preventDefault()}
          title={n} onClick={() => { haptic.tap(); const head = value.slice(0, cut); onChange(`${head}${head && !/[\n ]$/.test(head) ? ' ' : ''}${n}`); }}>{n}</button>
      ))}
    </div>
  );
}

/** «Что помогало с этим у других»: from a theme chip in a player's diary. */
export function ThemeHelpSheet({ clientId, theme, onClose }: { clientId: string; theme: string; onClose: () => void }) {
  const t = useT();
  const axes = useAxes();
  const [data, setData] = useState<{ skill: string | null; drills: Drill[]; ideas: { name: string; why: string }[]; catalogue?: CatalogueRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<typeof data>('/api/coach/drills/help', { method: 'POST', body: JSON.stringify({ theme }) })
      .then(setData).catch((err) => setError(err.message === 'ai_not_configured' ? t('dr.noAi') : t('co.failed', { message: err.message })));
  }, [theme]);
  async function toPlan(name: string) {
    try {
      const r = await api<{ startsAt: string }>(`/api/coach/clients/${clientId}/next-plan`, { method: 'POST', body: JSON.stringify({ name }) });
      haptic.success();
      await alert(t('dr.added', { when: fmtLongDay(new Date(r.startsAt)) }));
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'no_next_session' ? t('dr.noNext') : t('co.failed', { message: err.message }));
    }
  }
  const skill = axes.find((a) => a.id === data?.skill);
  return (
    <PinnedSheet title={t('dr.helpTitle')} onClose={onClose}
      footer={<button className="paybar__opt" onClick={onClose}>{t('dr.close')}</button>}>
      <div className="card__when">«{theme}»{skill ? ` · ${axisName(skill)}` : ''}</div>
      {error ? <div className="warnline">{error}</div> : !data ? (
        <><div className="card__when" style={{ marginTop: 10 }}>{t('dr.helpLoading')}</div><div className="skeleton" /></>
      ) : (
        <div className="dr-list">
          {data.drills.length === 0 && <div className="card__when">{t('dr.helpNone')}</div>}
          {data.drills.map((d) => (
            <div key={d.id} className="dr-row dr-row--static">
              <b>{d.name}</b>
              <span className="dr-meta"><Stats d={d} /></span>
              <button className="sess__link" onClick={() => toPlan(d.name)}>{t('dr.toPlan')}</button>
            </div>
          ))}
          {(data.catalogue ?? []).length > 0 && <div className="dr-group__head">{t('cat.section')}<span>{data.catalogue!.length}</span></div>}
          {(data.catalogue ?? []).map((c) => <CatalogueItem key={c.id} row={c} />)}
          {data.ideas.map((i) => (
            <div key={i.name} className="dr-row dr-row--static dr-row--new">
              <b>{i.name} <span className="dr-chip dr-chip--new">{t('dr.new')}</span></b>
              <span className="dr-meta">{i.why}</span>
              <button className="sess__link" onClick={() => toPlan(i.name)}>{t('dr.toPlan')}</button>
            </div>
          ))}
        </div>
      )}
    </PinnedSheet>
  );
}

interface Proposal {
  id: string;
  old: { name: string; name_other: string | null; skills: string[]; general: boolean };
  proposed: { name: string; name_other: string; skills: string[]; general: boolean };
}
interface MergeProposal { from: string; into: string; fromName: string; intoName: string }
type RowState = 'open' | 'editing' | 'saving' | 'saved' | 'skipped' | 'gone' | { error: string };

/**
 * «Привести в порядок»: what the model proposes, row by row. Nothing is
 * saved until a row is accepted; «Принять все» accepts what is still open,
 * duplicates first (so a renamed duplicate doesn't collide with its twin).
 */
function TidySheet({ axes, onClose }: { axes: Axis[]; onClose: () => void }) {
  const t = useT();
  const [data, setData] = useState<{ changes: Proposal[]; merges: MergeProposal[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<Record<string, RowState>>({});
  const [edits, setEdits] = useState<Record<string, { name: string; name_other: string }>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<{ changes: Proposal[]; merges: MergeProposal[] }>('/api/coach/drills/tidy', { method: 'POST', body: '{}' })
      .then(setData).catch((err) => setError(err.message === 'ai_not_configured' ? t('dr.noAi') : t('co.failed', { message: err.message })));
  }, []);
  const set = (key: string, v: RowState) => setState((s) => ({ ...s, [key]: v }));
  const failText = (code: string) => (code === 'taken' ? t('dr.taken') : code === 'unknown_drill' ? t('dr.mergedAway') : t('co.failed', { message: code }));
  const skillNames = (ids: string[]) => ids.map((id) => axes.find((a) => a.id === id)).filter(Boolean).map((a) => axisName(a!)).join(', ');

  async function acceptChange(p: Proposal): Promise<boolean> {
    const v = edits[p.id] ?? p.proposed;
    set(p.id, 'saving');
    try {
      await api(`/api/coach/drills/${p.id}`, { method: 'PUT', body: JSON.stringify({
        name: v.name, name_other: v.name_other, skills: p.proposed.general ? [] : p.proposed.skills, general: p.proposed.general }) });
      set(p.id, 'saved'); return true;
    } catch (err: any) { set(p.id, { error: failText(err.message) }); return false; }
  }
  async function acceptMerge(m: MergeProposal): Promise<boolean> {
    const key = `m:${m.from}`;
    set(key, 'saving');
    try {
      await api(`/api/coach/drills/${m.from}/merge`, { method: 'POST', body: JSON.stringify({ into: m.into }) });
      set(key, 'saved');
      // The merged-away drill's own rename has nothing left to apply to.
      if (data?.changes.some((c) => c.id === m.from)) set(m.from, 'gone');
      return true;
    } catch (err: any) { set(key, { error: failText(err.message) }); return false; }
  }
  const isOpen = (k: string) => { const s = state[k]; return !s || s === 'open' || s === 'editing' || typeof s === 'object'; };
  const openMerges = (data?.merges ?? []).filter((m) => isOpen(`m:${m.from}`));
  const openChanges = (data?.changes ?? []).filter((c) => isOpen(c.id));
  const openCount = openMerges.length + openChanges.length;

  async function acceptAll() {
    setBusy(true);
    const keys = [...openMerges.map((m) => `m:${m.from}`), ...openChanges.map((c) => c.id)];
    keys.forEach((k) => set(k, 'saving'));
    try {
      const { results } = await api<{ results: Record<string, string> }>('/api/coach/drills/tidy/apply', { method: 'POST', body: JSON.stringify({
        merges: openMerges.map((m) => ({ from: m.from, into: m.into })),
        changes: openChanges.map((c) => {
          const v = edits[c.id] ?? c.proposed;
          return { id: c.id, name: v.name, name_other: v.name_other, skills: c.proposed.general ? [] : c.proposed.skills, general: c.proposed.general };
        }),
      }) });
      for (const k of keys) {
        const r = results[k];
        set(k, r === 'saved' ? 'saved' : r === 'gone' ? 'gone' : { error: failText(r ?? 'failed') });
      }
      if (keys.every((k) => results[k] === 'saved' || results[k] === 'gone')) haptic.success(); else haptic.error();
    } catch (err: any) {
      keys.forEach((k) => set(k, { error: failText(err.message) }));
      haptic.error();
    } finally { setBusy(false); }
  }

  const status = (k: string) => {
    const s = state[k];
    if (s === 'saved') return <span className="dr-done">{t('dr.accepted')}</span>;
    if (s === 'skipped') return <span className="dr-done dr-done--skip">{t('dr.skipped')}</span>;
    if (s === 'gone') return <span className="dr-done dr-done--skip">{t('dr.mergedAway')}</span>;
    return null;
  };
  const buttons = (k: string, accept: () => void, edit?: () => void) => (
    <div className="dr-acts">
      <button className="act act--good" disabled={busy || state[k] === 'saving'} onClick={() => { haptic.tap(); accept(); }}>{t('dr.accept')}</button>
      {edit && <button className="act" disabled={busy} onClick={() => { haptic.tap(); edit(); }}>{t('dr.change')}</button>}
      <button className="act" disabled={busy} onClick={() => { haptic.tap(); set(k, 'skipped'); }}>{t('dr.skip')}</button>
    </div>
  );

  return (
    <PinnedSheet title={t('dr.tidy')} onClose={onClose} full
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('dr.close')}</button>
        <button className="savebtn" disabled={busy || !data || openCount === 0} onClick={acceptAll}>{t('dr.acceptAll', { n: openCount })}</button>
      </>}>
      <div className="card__when">{t('dr.tidyHint')}</div>
      {error ? <div className="warnline">{error}</div> : !data ? (
        <><div className="card__when">{t('dr.tidyLoading')}</div><div className="skeleton" /></>
      ) : data.changes.length + data.merges.length === 0 ? (
        <div className="card__when">{t('dr.tidyNone')}</div>
      ) : (<>
        {data.merges.length > 0 && <div className="mf__label dr-sec">{t('dr.tidyMerges')}</div>}
        {data.merges.map((m) => {
          const k = `m:${m.from}`, s = state[k];
          return (
            <div key={k} className={`dr-card dr-prop${isOpen(k) ? '' : ' dr-prop--done'}`}>
              <b className="dr-prop__name">«{m.fromName}»</b>
              <span className="dr-prop__other">{t('dr.mergeRow', { b: m.intoName })}</span>
              {typeof s === 'object' && <div className="warnline">{s.error}</div>}
              {isOpen(k) ? buttons(k, () => acceptMerge(m)) : status(k)}
            </div>
          );
        })}
        {data.changes.length > 0 && <div className="mf__label dr-sec">{t('dr.tidyNames')}</div>}
        {data.changes.map((p) => {
          const s = state[p.id], e = edits[p.id];
          const added = p.proposed.skills.filter((x) => !p.old.skills.includes(x));
          return (
            <div key={p.id} className={`dr-card dr-prop${isOpen(p.id) ? '' : ' dr-prop--done'}`}>
              {p.old.name !== p.proposed.name && <span className="dr-prop__old">{p.old.name}</span>}
              {s === 'editing' ? (<>
                <input className="notearea dr-input" value={e?.name ?? p.proposed.name} aria-label={t('dr.name')}
                  onChange={(ev) => setEdits({ ...edits, [p.id]: { name: ev.target.value, name_other: e?.name_other ?? p.proposed.name_other } })} />
                <input className="notearea dr-input" value={e?.name_other ?? p.proposed.name_other} aria-label={t('dr.nameOther')}
                  onChange={(ev) => setEdits({ ...edits, [p.id]: { name: e?.name ?? p.proposed.name, name_other: ev.target.value } })} />
              </>) : (<>
                <b className="dr-prop__name">{p.old.name !== p.proposed.name && '→ '}{e?.name ?? p.proposed.name}</b>
                {(e?.name_other ?? p.proposed.name_other) && <span className="dr-prop__other">{e?.name_other ?? p.proposed.name_other}</span>}
              </>)}
              {added.length > 0 && <span className="dr-chip dr-prop__skills">{t('dr.addSkills', { list: skillNames(added) })}</span>}
              {p.proposed.general && !p.old.general && <span className="dr-chip dr-chip--general dr-prop__skills">{t('dr.general')}</span>}
              {typeof s === 'object' && <div className="warnline">{s.error}</div>}
              {isOpen(p.id) ? buttons(p.id, () => acceptChange(p),
                s === 'editing' ? undefined : () => { setEdits({ ...edits, [p.id]: e ?? { name: p.proposed.name, name_other: p.proposed.name_other } }); set(p.id, 'editing'); })
                : status(p.id)}
            </div>
          );
        })}
      </>)}
    </PinnedSheet>
  );
}

/* ---------------------------------------------------------------- the shared catalogue */

interface CatalogueState {
  shares: boolean; authorName: string | null; drills: number; inCatalogue: number;
  contribution: { entries: number; coaches: number; players: number; improved: number } | null;
  badge: { month: string; name: string } | null;
}
export interface CatalogueRow {
  id: string; name: string; name_other: string; skill: string; author: string | null;
  description: Partial<Description> | null; coaches: number; players: number; improved: number;
}

/** The drill card's «у других тренеров» line. */
function OthersLine({ drillId }: { drillId: string }) {
  const t = useT();
  const [o, setO] = useState<{ hidden?: true; entry?: string | null; coaches?: number; players?: number; improved?: number } | null>(null);
  useEffect(() => { api<any>(`/api/coach/catalogue/drill/${drillId}`).then(setO).catch(() => setO({ entry: null })); }, [drillId]);
  if (!o) return <div className="dr-block__empty">…</div>;
  if (o.hidden) return <div className="dr-block__empty">{t('cat.othersHidden')}</div>;
  if (!o.entry) return <div className="dr-block__empty">{t('cat.othersNone')}</div>;
  const n = o.coaches ?? 0, coaches = t.word('cat.coaches', n);
  return (o.players ?? 0) > 0
    ? <div className="dr-meta"><span className="dr-result">{t('cat.others', { k: o.improved ?? 0, m: o.players ?? 0, n, coaches })}</span></div>
    : <div className="dr-block__empty">{t('cat.othersNoResult', { n, coaches })}</div>;
}

/** One catalogue entry: «каталог», the author, its result, and «Добавить к себе». */
function CatalogueItem({ row, checked, onCheck }: { row: CatalogueRow; checked?: boolean; onCheck?: (v: boolean) => void }) {
  const t = useT();
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  async function add() {
    setBusy(true);
    try { await api('/api/coach/catalogue/add', { method: 'POST', body: JSON.stringify({ ids: [row.id] }) }); haptic.success(); setAdded(true); }
    catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  const first = firstLine((row.description ?? null) as Description | null);
  return (
    <div className={`dr-row dr-row--static cat-row${onCheck ? ' cat-row--check' : ''}`}>
      {onCheck && <input type="checkbox" className="cat-row__box" checked={!!checked} onChange={(e) => onCheck(e.target.checked)} aria-label={row.name} />}
      <span className="cat-row__main">
        <b className="dr-row__name">{row.name} <span className="dr-chip cat-tag">{t('cat.tag')}</span></b>
        <span className="dr-row__other">{row.name_other}</span>
        {first && <span className="dr-row__desc">{first}</span>}
        <span className="dr-meta">
          <span>{row.author ? t('cat.by', { name: row.author }) : t('cat.byAnon')}</span>
          {/* Nobody has used it yet: no «0 тренеров». */}
          {row.coaches > 0 && (
            <span>{row.players > 0 ? <b className="dr-result">{t('cat.result', { k: row.improved, m: row.players })}</b> : null}
              {row.players > 0 ? ' · ' : ''}{t('cat.coachesN', { n: row.coaches, coaches: t.word('cat.coaches', row.coaches) })}</span>
          )}
        </span>
        {!onCheck && (added
          ? <span className="dr-done">{t('cat.added')}</span>
          : <button className="sess__link" disabled={busy} onClick={add}>{t('cat.add')}</button>)}
      </span>
    </div>
  );
}

/**
 * Настройки → Упражнения → «Делиться упражнениями»: the switch, what is
 * shared, «Указать моё имя», «Ваш вклад», the month's badge, and the
 * coach's drills not in the catalogue yet.
 */
function CatalogueSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [s, setS] = useState<CatalogueState | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const load = () => api<CatalogueState>('/api/coach/catalogue').then((x) => { setS(x); setName(x.authorName ?? ''); }).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  async function toggle() {
    if (!s) return;
    setBusy(true);
    try {
      const x = await api<CatalogueState>('/api/coach/catalogue/sharing', { method: 'PUT', body: JSON.stringify({ on: !s.shares }) });
      setS(x); haptic.success();
      // Switched on with drills not in the catalogue yet: straight to «это то же упражнение?».
      if (x.shares && x.drills > x.inCatalogue) setPreview(true);
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  async function saveName() {
    try { await api('/api/coach/catalogue/author', { method: 'PUT', body: JSON.stringify({ name }) }); haptic.success(); load(); }
    catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
  }
  if (preview) return <SharePreview onClose={() => { setPreview(false); load(); }} />;
  const c = s?.contribution;
  return (
    <PinnedSheet title={t('cat.title')} onClose={onClose} full
      footer={<button className="paybar__opt" onClick={onClose}>{t('dr.close')}</button>}>
      {!s ? <div className="skeleton" /> : (<>
        <button className={`cat-card${s.shares ? ' cat-card--on' : ''}`} disabled={busy} onClick={toggle} role="switch" aria-checked={s.shares}>
          <span className="cat-card__main"><b>{t('cat.share')}</b>
            <small>{s.shares ? t('cat.shareOn', { n: s.inCatalogue, m: s.drills }) : t('cat.shareOff')}</small></span>
          <span className={`cat-switch${s.shares ? ' cat-switch--on' : ''}`} aria-hidden="true"><i /></span>
        </button>
        <div className="card__when">{t('cat.what')}</div>
        <div className="card__when">{t('cat.give')}</div>
        {s.shares && (<>
          <div className="dr-card">
            <span className="mf__label">{t('cat.author')}</span>
            <input className="notearea dr-input" value={name} maxLength={60} placeholder={t('cat.authorPh')} onChange={(e) => setName(e.target.value)} />
            {!s.authorName && <span className="card__when">{t('cat.anon')}</span>}
            {name.trim() !== (s.authorName ?? '') && <button className="paybar__opt" onClick={saveName}>{t('cat.authorSave')}</button>}
          </div>
          <div className="dr-card">
            <span className="mf__label">{t('cat.contrib')}</span>
            {c && c.coaches > 0 ? (
              <span className="cat-contrib">{t('cat.contribLine', { n: c.coaches, coaches: t.word('cat.coaches', c.coaches) })}
                {c.players > 0 && t('cat.contribPct', { pct: Math.round((c.improved / c.players) * 100) })}</span>
            ) : <span className="card__when">{t('cat.contribNone')}</span>}
            {s.badge && <span className="cat-badge">{t('cat.badge', { name: s.badge.name })}</span>}
          </div>
          {s.drills > s.inCatalogue ? (
            <div className="dr-card">
              <span>{t('cat.notShared', { n: s.drills - s.inCatalogue })}</span>
              <button className="paybar__opt" onClick={() => { haptic.tap(); setPreview(true); }}>{t('cat.send')}</button>
            </div>
          ) : <div className="card__when">{t('cat.nothing')}</div>}
        </>)}
      </>)}
    </PinnedSheet>
  );
}

interface Preview {
  matches: { drillId: string; drillName: string; entryId: string; entryName: string; name_ru: string; name_en: string; skill: string }[];
  fresh: { drillId: string; drillName: string; name_ru: string; name_en: string; skill: string }[];
}

/** «Отправить в каталог»: «это то же упражнение?» for each match, then «Поделиться». */
function SharePreview({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [p, setP] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [same, setSame] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<Preview>('/api/coach/catalogue/share-preview', { method: 'POST', body: '{}' })
      .then((x) => { setP(x); setSame(Object.fromEntries(x.matches.map((m) => [m.drillId, true]))); })
      .catch((err) => setError(err.message === 'ai_not_configured' ? t('dr.noAi') : t('co.failed', { message: err.message })));
  }, []);
  async function share() {
    if (!p) return;
    setBusy(true);
    try {
      const r = await api<{ linked: number; created: number }>('/api/coach/catalogue/share', { method: 'POST', body: JSON.stringify({
        matches: p.matches.map((m) => ({ ...m, same: same[m.drillId] !== false })), fresh: p.fresh,
      }) });
      haptic.success();
      await alert(t('cat.shared', { linked: r.linked, created: r.created }));
      onClose();
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  return (
    <PinnedSheet title={t('cat.previewTitle')} onClose={onClose} full
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('dr.cancel')}</button>
        <button className="savebtn" disabled={!p || busy || p.matches.length + p.fresh.length === 0} onClick={share}>{t('cat.shareBtn')}</button>
      </>}>
      {error ? <div className="warnline">{error}</div> : !p ? (<><div className="card__when">{t('cat.reading')}</div><div className="skeleton" /></>) : (<>
        {p.matches.length > 0 && <div className="dr-group__head">{t('cat.sameQ')}<span>{p.matches.length}</span></div>}
        {p.matches.map((m) => (
          <div key={m.drillId} className="dr-card cat-match">
            <span><b>{m.drillName}</b> ≈ <b>{m.entryName}</b></span>
            <span className="cat-yn">
              <button className="chip" aria-pressed={same[m.drillId] !== false} onClick={() => setSame({ ...same, [m.drillId]: true })}>{t('cat.yes')}</button>
              <button className="chip" aria-pressed={same[m.drillId] === false} onClick={() => setSame({ ...same, [m.drillId]: false })}>{t('cat.no')}</button>
            </span>
          </div>
        ))}
        <div className="dr-group__head">{t('cat.newOnes', { n: p.fresh.length + p.matches.filter((m) => same[m.drillId] === false).length })}</div>
        <div className="dr-list">
          {p.fresh.map((f) => (
            <div key={f.drillId} className="dr-row dr-row--static"><b className="dr-row__name">{f.drillName}</b>
              <span className="dr-row__other">{f.name_ru === f.drillName ? f.name_en : f.name_ru}</span></div>
          ))}
        </div>
      </>)}
    </PinnedSheet>
  );
}

/** «Первые шаги» → «Упражнения из каталога»: the top 20 by result, ticked, «Добавить N упражнений». */
export function CatalogueTopSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [rows, setRows] = useState<CatalogueRow[] | null>(null);
  const [off, setOff] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const load = () => api<{ entries: CatalogueRow[] }>('/api/coach/catalogue/top')
    .then((r) => { setRows(r.entries); setPicked(new Set(r.entries.map((e) => e.id))); setOff(false); })
    .catch((err) => { if (err.message === 'not_sharing') setOff(true); else setRows([]); });
  useEffect(() => { load(); }, []);
  async function turnOn() {
    setBusy(true);
    try { await api('/api/coach/catalogue/sharing', { method: 'PUT', body: JSON.stringify({ on: true }) }); haptic.success(); await load(); }
    catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  async function add() {
    setBusy(true);
    try {
      const r = await api<{ added: number }>('/api/coach/catalogue/add', { method: 'POST', body: JSON.stringify({ ids: [...picked] }) });
      haptic.success(); await alert(t('cat.addedN', { n: r.added })); onClose();
    } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  return (
    <PinnedSheet title={t('cat.topTitle')} onClose={onClose} full
      footer={off ? <button className="savebtn" disabled={busy} onClick={turnOn}>{t('cat.turnOn')}</button> : <>
        <button className="paybar__opt" onClick={onClose}>{t('dr.cancel')}</button>
        <button className="savebtn" disabled={busy || !picked.size} onClick={add}>{t('cat.addN', { n: picked.size })}</button>
      </>}>
      {off ? (<><div className="card__when">{t('cat.what')}</div><div className="card__when">{t('cat.give')}</div></>)
        : !rows ? <div className="skeleton" /> : rows.length === 0 ? <div className="card__when">{t('cat.empty')}</div> : (<>
          <div className="card__when">{t('cat.topHint')}</div>
          <div className="dr-list">
            {rows.map((r) => (
              <CatalogueItem key={r.id} row={r} checked={picked.has(r.id)}
                onCheck={(v) => { const n = new Set(picked); if (v) n.add(r.id); else n.delete(r.id); setPicked(n); }} />
            ))}
          </div>
        </>)}
    </PinnedSheet>
  );
}
